import { type TFunction } from 'i18next';
import { type ActivityEntry, gitChangeRequestLabel, gitStatusCauseOf } from '@apm/contracts';

/**
 * Dòng lịch sử của một lần đổi trạng thái **do luật git** gây ra — APM-GIT-h,
 * docs/plan-git-integration.md §6.2:
 *
 * > **Git** chuyển sang *Xong* — MR !45 «Sửa đăng nhập» đã merge vào `main`
 *
 * Event ấy chạy dưới `system`, nên không có hàm này nó đọc là "Hệ thống đã đổi
 * trạng thái" — như thể ChillPM tự sửa việc của người ta. Mọi thứ cần để kể đã
 * nằm trong chính event (`payload.meta.cause` là bản chụp lúc luật chạy), nên
 * không có lời gọi nào thêm.
 *
 * `null` = không phải dòng do git ⇒ nơi gọi vẽ như mọi dòng khác.
 */
export function gitActivityLine(
  entry: Pick<ActivityEntry, 'eventType' | 'payload'>,
  t: TFunction,
): { actor: string; text: string } | null {
  if (entry.eventType !== 'task.status_changed') return null;
  const cause = gitStatusCauseOf(entry.payload);
  if (!cause) return null;

  const after = entry.payload['after'];
  const status =
    after !== null && typeof after === 'object'
      ? String((after as { statusName?: unknown }).statusName ?? '')
      : '';
  // `work_started` kể theo thứ gây ra nó (nhánh hay commit); các trigger còn lại
  // đều là chuyện của một MR.
  const key =
    cause.trigger === 'work_started' ? `work_started_${cause.subjectKind}` : cause.trigger;
  const reason = t(`activity.gitRule.${key}`, {
    ref: cause.number === null ? '' : gitChangeRequestLabel(cause.hostKind, cause.number),
    title: cause.title ?? '',
    branch: cause.branch ?? cause.subjectRef,
    sha: cause.subjectRef.slice(0, 8),
  });
  return {
    actor: t('collaboration.gitActor'),
    text: t('activity.gitRule.moved', { status, reason }),
  };
}
